/**
 * ADD DISPLAY AD GROUPS — /google-ads/awareness/create/adgroup[?campaign_id=…]
 *
 * One existing Display campaign, many ad-group drafts — each with its
 * audience + content targeting and its responsive display ads — created in
 * order. New ads start from the campaign's existing brand (landing page,
 * business name, logo asset). Resumable: created groups and ads are
 * remembered by draft id.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Plus } from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Select } from '@/components/Select';
import { googleAdsService, GoogleAdsCampaignSummary } from '@/services/google-ads.service';
import { BackTo, biddingLabel, errMsg, formatMicros, Pill, Section, statusOf, Tag, useYoutubeAuth } from '@/components/google-ads/youtube/youtubeShared';
import { ImageDraft, Pub, uid } from '@/components/google-ads/objectives/objectiveShared';
import { ConnectFirst, GoalStrip, PageSpinner, SaveBar } from '@/components/google-ads/objectives/wizardChrome';
import {
  bidModeForCampaign, cloneDisplayGroup, DisplayGroupCard, DisplayGroupDraft, displayGroupIssues, displayGroupPayload, isDisplayCampaign,
  newDisplayGroup, publishRda, RdaBrand, RdaDraft, rdaIssues, RdaPreview, writeRdaAsset,
} from '@/components/google-ads/display/displayShared';
import { AWARENESS_OBJECTIVE as CFG } from './awarenessObjective';

type Patch<T> = Partial<T> | ((cur: T) => Partial<T>);
const filled = (xs: string[]) => xs.map((x) => x.trim()).filter(Boolean);

export default function CreateAwarenessAdgroup() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { auth, accountUid } = useYoutubeAuth();
  const ready = !!(auth?.connected && auth?.customer_selected && accountUid);
  const currency = auth?.currency ?? '';

  const [campaigns, setCampaigns] = useState<GoogleAdsCampaignSummary[] | null>(null);
  const [campaignId, setCampaignId] = useState(params.get('campaign_id') || '');
  const [existingGroups, setExistingGroups] = useState<number | null>(null);
  const [brand, setBrand] = useState<Partial<RdaBrand> | undefined>(undefined);
  const campaign = campaigns?.find((c) => c.id === campaignId);
  const bidMode = bidModeForCampaign(campaign?.biddingStrategyType);

  const first = useMemo(() => newDisplayGroup(1), []);
  const [groups, setGroups] = useState<DisplayGroupDraft[]>([first]);
  const [openGroup, setOpenGroup] = useState(first.id);
  const [activeAd, setActiveAd] = useState(first.ads[0].id);
  const patchGroup = (gid: string, p: Patch<DisplayGroupDraft>) =>
    setGroups((gs) => gs.map((g) => (g.id === gid ? { ...g, ...(typeof p === 'function' ? p(g) : p) } : g)));
  const patchAd = (gid: string, aid: string, p: Patch<RdaDraft>) =>
    patchGroup(gid, (g) => ({ ads: g.ads.map((a) => (a.id === aid ? { ...a, ...(typeof p === 'function' ? p(a) : p) } : a)) }));

  const [pub, setPub] = useState<Record<string, Pub>>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const madeRef = useRef<{ groups: Record<string, string>; ads: Record<string, string> }>({ groups: {}, ads: {} });

  useEffect(() => {
    if (!accountUid) return;
    googleAdsService.listCampaigns(accountUid)
      .then((l) => setCampaigns(l.filter((c) => isDisplayCampaign(c) && c.status !== 'REMOVED')))
      .catch(() => setCampaigns([]));
  }, [accountUid]);

  // The campaign's existing ads carry its brand: landing page, business name and logo assets (reused, never re-uploaded).
  useEffect(() => {
    setExistingGroups(null); setBrand(undefined);
    if (!accountUid || !campaignId) return;
    let alive = true;
    Promise.all([
      googleAdsService.listAdGroups(accountUid, campaignId).catch(() => []),
      googleAdsService.display.listAds(accountUid, { campaignId }).catch(() => []),
    ]).then(([gs, ads]) => {
      if (!alive) return;
      setExistingGroups(gs.filter((g) => g.status !== 'REMOVED').length);
      const ad = ads[0];
      if (!ad) return;
      const asDraft = (x: { resourceName: string; url?: string; width?: number; height?: number; name?: string }): ImageDraft =>
        ({ id: uid(), url: x.url ?? '', note: x.name ?? 'logo', width: x.width, height: x.height, resourceName: x.resourceName });
      const b: Partial<RdaBrand> = {
        finalUrl: ad.finalUrls[0] ?? '', businessName: ad.businessName ?? '', cta: ad.callToActionText ?? '',
        squareLogos: ad.squareLogos.filter((l) => l.url).map(asDraft), wideLogos: ad.logos.filter((l) => l.url).map(asDraft),
      };
      setBrand(b);
      setGroups((cur) => cur.map((g) => ({ ...g, ads: g.ads.map((a) => ({
        ...a,
        finalUrl: a.finalUrl || b.finalUrl || '', businessName: a.businessName || b.businessName || '',
        squareLogos: a.squareLogos.length ? a.squareLogos : (b.squareLogos ?? []).map((i) => ({ ...i })),
        wideLogos: a.wideLogos.length ? a.wideLogos : (b.wideLogos ?? []).map((i) => ({ ...i })),
      })) })));
    });
    return () => { alive = false; };
  }, [accountUid, campaignId]);

  const issues = useMemo(() => {
    const i: string[] = [];
    if (!campaignId) i.push('Pick the campaign these ad groups belong to.');
    groups.forEach((g, n) => {
      const label = g.name.trim() || `Ad group ${n + 1}`;
      const gi = displayGroupIssues(g, bidMode);
      if (gi.length) i.push(`${label}: needs ${gi.join(', ')}.`);
      g.ads.forEach((a, k) => { const ai = rdaIssues(a); if (ai.length) i.push(`${label} → ${a.name.trim() || `Ad ${k + 1}`}: needs ${ai.join(', ')}.`); });
    });
    return i;
  }, [campaignId, groups, bidMode]);

  const submit = async () => {
    if (!accountUid || issues.length) return;
    setSaving(true); setError(null);
    const mark = (k: string, v: Pub) => setPub((m) => ({ ...m, [k]: v }));
    const made = madeRef.current;
    try {
      for (const g of groups) {
        if (!made.groups[g.id]) {
          mark(g.id, 'running');
          try {
            const r = await googleAdsService.display.createAdGroup(accountUid, displayGroupPayload(campaignId, g, bidMode));
            made.groups[g.id] = r.adGroupId;
          } catch (e) { mark(g.id, 'failed'); setError(`Ad group "${g.name.trim()}": ${errMsg(e)}`); return; }
          mark(g.id, 'done');
        }
        for (const a of g.ads) {
          if (made.ads[a.id]) continue;
          mark(a.id, 'running');
          try {
            const r = await publishRda(accountUid, made.groups[g.id], a, (field, id, rn) => patchAd(g.id, a.id, writeRdaAsset(field, id, rn)));
            made.ads[a.id] = r.resourceName;
          } catch (e) { mark(a.id, 'failed'); setError(`Ad "${filled(a.headlines)[0] ?? a.name}" in ${g.name.trim()}: ${errMsg(e)}`); return; }
          mark(a.id, 'done');
        }
      }
      toast.success(groups.length === 1 ? 'Ad group created — paused until you enable it.' : `${groups.length} ad groups created — paused until you enable them.`);
      navigate(CFG.base);
    } finally { setSaving(false); }
  };

  const allAds = groups.flatMap((g) => g.ads);
  const active = allAds.find((a) => a.id === activeAd) ?? allAds[0] ?? null;
  const st = campaign ? statusOf(campaign.status, campaign.primaryStatus) : null;
  const locked = Object.keys(madeRef.current.groups).length > 0;

  return (
    <>
      <Topbar title="New display ad groups" breadcrumb={`Google Ads / ${CFG.title}`}
        description="Each ad group is one audience — its own targeting and responsive display ads"
        back={<BackTo to={CFG.base} label={`Back to ${CFG.title}`} />} />

      <div className="w-full px-7 py-7">
        {auth === undefined && <PageSpinner />}
        {auth !== undefined && !ready && <ConnectFirst what="ad groups" />}

        {ready && (
          <div className="flex w-full gap-5 pb-12">
            <div className="min-w-0 flex-1 space-y-4">
              <GoalStrip items={[
                { label: 'Goal', value: CFG.goal },
                { label: 'Type', value: 'Display ad groups' },
                { label: 'Adding to', value: campaign?.name ?? 'pick a campaign' },
              ]}>
                <Tag>starts paused</Tag>
              </GoalStrip>

              <Section title="Campaign" hint="The Display campaign these ad groups belong to.">
                <Select className="h-10 rounded-lg" value={campaignId} onChange={(e) => setCampaignId(e.target.value)} disabled={locked}>
                  <option value="">{campaigns === null ? 'Loading campaigns…' : campaigns.length ? 'Pick a Display campaign' : 'No Display campaigns yet — create one first'}</option>
                  {(campaigns ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </Select>
                {campaign && st && (
                  <div className="mt-3 flex flex-wrap items-center gap-2 text-2xs text-text-muted">
                    <Pill {...st} />
                    {biddingLabel(campaign.biddingStrategyType) && <Tag>{biddingLabel(campaign.biddingStrategyType)}</Tag>}
                    {formatMicros(campaign.budgetAmountMicros, currency) && <Tag>{formatMicros(campaign.budgetAmountMicros, currency)} / day</Tag>}
                    {existingGroups !== null && <span>{existingGroups} ad group{existingGroups === 1 ? '' : 's'} already</span>}
                    {bidMode !== 'none' && <span className="text-text-secondary">· each new group needs a {bidMode === 'cpm' ? 'max CPM' : 'max CPC'}</span>}
                    {brand?.finalUrl && <span>· new ads start from {brand.finalUrl.replace(/^https?:\/\//, '')}</span>}
                  </div>
                )}
              </Section>

              {groups.map((g, n) => (
                <DisplayGroupCard key={g.id} group={g} index={n} open={openGroup === g.id} onToggle={() => setOpenGroup(openGroup === g.id ? '' : g.id)}
                  bidMode={bidMode} currency={currency} pub={pub} madeGroup={!!madeRef.current.groups[g.id]} madeAd={(id) => !!madeRef.current.ads[id]}
                  busy={saving} onChange={(p) => patchGroup(g.id, p)} brand={brand}
                  onDuplicate={() => { const c = cloneDisplayGroup(g); setGroups((gs) => { const i = gs.findIndex((x) => x.id === g.id); return [...gs.slice(0, i + 1), c, ...gs.slice(i + 1)]; }); setOpenGroup(c.id); }}
                  onRemove={groups.length > 1 ? () => setGroups((gs) => gs.filter((x) => x.id !== g.id)) : undefined}
                  activeAdId={active?.id} onFocusAd={setActiveAd} />
              ))}

              <Button variant="secondary" className="w-full" iconLeft={<Plus size={15} />} disabled={saving}
                onClick={() => { const g = newDisplayGroup(groups.length + 1, brand); setGroups((gs) => [...gs, g]); setOpenGroup(g.id); setActiveAd(g.ads[0].id); }}>
                Add another ad group
              </Button>

              <SaveBar issues={issues} error={error} saving={saving} onSave={submit} onCancel={() => navigate(CFG.base)}
                label={groups.length === 1 ? 'Create ad group' : `Create ${groups.length} ad groups`} />
            </div>

            <div className="hidden w-[320px] shrink-0 xl:block">
              <div className="sticky top-24"><RdaPreview ad={active} /></div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
